import { useState, useEffect } from 'react';
import { Calendar, CheckCircle, XCircle, Edit, AlertTriangle, Filter, ArrowLeft, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FinanceTimesheets = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('submitted');
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [editedHours, setEditedHours] = useState({});
  const [filterPeriod, setFilterPeriod] = useState('week_to_date');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [revisionNote, setRevisionNote] = useState('');
  const [showRevisionDialog, setShowRevisionDialog] = useState(false);

  useEffect(() => {
    fetchTimesheets();
  }, [activeTab, filterPeriod]);

  const fetchTimesheets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        status: getStatusForTab(activeTab),
        period: filterPeriod,
        ...(filterPeriod === 'custom' && customDateRange.start && customDateRange.end && {
          start_date: customDateRange.start,
          end_date: customDateRange.end
        })
      });

      const res = await fetch(`${BACKEND_URL}/api/timesheets/finance?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setTimesheets(data || []);
      } else {
        setTimesheets([]);
      }
    } catch (error) {
      console.error('Failed to load timesheets:', error);
      toast.error('Failed to load timesheets');
      setTimesheets([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusForTab = (tab) => {
    switch(tab) {
      case 'submitted': return 'submitted';
      case 'revision_required': return 'revision_required';
      case 'approved': return 'approved';
      default: return 'submitted';
    }
  };

  const handleApprove = async (timesheetId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/timesheets/${timesheetId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Timesheet approved for payment');
        fetchTimesheets();
      } else {
        throw new Error('Failed to approve');
      }
    } catch (error) {
      toast.error('Failed to approve timesheet');
    }
  };

  const handleRequestRevision = (timesheet) => {
    setSelectedTimesheet(timesheet);
    setRevisionNote('');
    setShowRevisionDialog(true);
  };

  const submitRevisionRequest = async () => {
    if (!revisionNote.trim()) {
      toast.error('Please provide a revision note');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/timesheets/${selectedTimesheet.id}/request-revision`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ note: revisionNote })
      });

      if (res.ok) {
        toast.success('Revision requested - sent back to manager');
        setShowRevisionDialog(false);
        fetchTimesheets();
      } else {
        throw new Error('Failed to request revision');
      }
    } catch (error) {
      toast.error('Failed to request revision');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6 overflow-x-hidden">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-4">
          <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Finance Timesheets Review</h1>
            <p className="text-sm text-gray-500">Review and approve timesheets for payment</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="border-b px-6">
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => setActiveTab('submitted')}
              className={`flex items-center gap-2 px-4 py-4 border-b-2 transition-colors ${
                activeTab === 'submitted'
                  ? 'border-blue-600 text-blue-600 font-semibold'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Clock className="w-4 h-4" />
              Submitted
              {activeTab === 'submitted' && timesheets.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                  {timesheets.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('revision_required')}
              className={`flex items-center gap-2 px-4 py-4 border-b-2 transition-colors ${
                activeTab === 'revision_required'
                  ? 'border-orange-600 text-orange-600 font-semibold'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <AlertTriangle className="w-4 h-4" />
              Revision Required
            </button>
            <button
              onClick={() => setActiveTab('approved')}
              className={`flex items-center gap-2 px-4 py-4 border-b-2 transition-colors ${
                activeTab === 'approved'
                  ? 'border-green-600 text-green-600 font-semibold'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <CheckCircle className="w-4 h-4" />
              Approved
            </button>
          </div>
        </div>

        {/* Filter */}
        <div className="p-6 border-b">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-gray-500" />
            <Select value={filterPeriod} onValueChange={setFilterPeriod}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week_to_date">Week to Date</SelectItem>
                <SelectItem value="last_week">Last Week</SelectItem>
                <SelectItem value="month_to_date">Month to Date</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
            
            {filterPeriod === 'custom' && (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                  className="w-40"
                />
                <span className="text-gray-500">to</span>
                <Input
                  type="date"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                  className="w-40"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Timesheets List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12 bg-white rounded-xl">
            <p className="text-gray-500">Loading timesheets...</p>
          </div>
        ) : timesheets.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl">
            <p className="text-gray-500">No timesheets found for this period</p>
          </div>
        ) : (
          timesheets.map((timesheet) => (
            <div key={timesheet.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {timesheet.employee_name}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      timesheet.status === 'approved' 
                        ? 'bg-green-100 text-green-700'
                        : timesheet.status === 'revision_required'
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {timesheet.status === 'revision_required' ? 'Revision Required' : 
                       timesheet.status === 'approved' ? 'Approved' : 'Submitted'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Week Period</p>
                      <p className="font-medium">{formatDate(timesheet.week_start)} - {formatDate(timesheet.week_end)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Total Hours</p>
                      <p className="font-medium">{timesheet.total_hours?.toFixed(2) || '0.00'} hrs</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Submitted</p>
                      <p className="font-medium">{formatDate(timesheet.submitted_at)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Department</p>
                      <p className="font-medium">{timesheet.department || 'N/A'}</p>
                    </div>
                  </div>
                  {timesheet.revision_note && (
                    <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-xs text-orange-800 font-medium">Revision Note:</p>
                      <p className="text-sm text-orange-900 mt-1">{timesheet.revision_note}</p>
                    </div>
                  )}
                  {timesheet.submission_notes && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="submission-notes-display">
                      <p className="text-xs text-blue-800 font-medium">Manager Notes:</p>
                      <p className="text-sm text-blue-900 mt-1">{timesheet.submission_notes}</p>
                    </div>
                  )}
                </div>
                
                {activeTab === 'submitted' && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleApprove(timesheet.id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => handleRequestRevision(timesheet)}
                      variant="outline"
                      className="border-orange-500 text-orange-600 hover:bg-orange-50"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Request Revision
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Revision Dialog */}
      <Dialog open={showRevisionDialog} onOpenChange={setShowRevisionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Revision</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              This timesheet will be sent back to the department manager for revision.
            </p>
            <div>
              <Label>Revision Note *</Label>
              <textarea
                value={revisionNote}
                onChange={(e) => setRevisionNote(e.target.value)}
                className="w-full mt-1 p-2 border rounded-lg min-h-24"
                placeholder="Explain what needs to be revised..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevisionDialog(false)}>
              Cancel
            </Button>
            <Button onClick={submitRevisionRequest} className="bg-orange-600 hover:bg-orange-700">
              Send for Revision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinanceTimesheets;
